import React from "react";
import "./TableFilter.css";
import TopFilterLabels from "./TopFilterLabels";
import amplitude from "../../utils/ampli";
import { CheckSquare, XSquare } from "lucide-react";

const DIRECT_SELECT_LABELS = new Set(["Needs/Surplus"]);
const EXCLUSION_SELECT_LABELS = new Set(["Confidence"]);
// Filters whose stored value is the EXCLUDED list but whose setFiltered expects
// the SELECTED list (the drawer inverts on write).
const EXCLUSION_STORED_IDS = new Set(["pricing_action_tags"]);

const TableFilter2 = ({ id, label, data, setFiltered, filtered, countsMap = {}, sort }) => {
  const value = label.toLowerCase();
  // Labels that should support contiguous range selection
  // const RANGE_LABELS = new Set(["Color", "Clarity", "Cut", "Polish", "Symmetry", "Fluor"]);
  const RANGE_LABELS = new Set(["Flor"]);

  if (!data) return null;

  const newData = data;
  const enumLabelMap = {
    "attr_types.all#custom_data.buyer_name": "Buyer",
    "attr_types.all#custom_data.internal_grading": "Spec",
    "attr_types.all#custom_data.memo_stock": "Memo Stock",
    "attr_types.all#custom_data.pcp_table_inc": "Table Inc",
    "attr_types.all#custom_data.pcp_side_inc": "Side Inc",
    "attr_types.all#custom_data.open_in_girdle": "Open in Girdle",
    "attr_types.all#custom_data.natural_in_crown": "Natural in Crown",
    "attr_types.all#custom_data.natural_in_pav": "Natural in Pav",
    "attr_types.all#custom_data.natural_in_girdle": "Natural in Girdle",
    "attr_types.all#custom_data.long_term_memo": "Long Term Memo",
    "stock_performance_data.bucket": "Bucket",
  };

const isCustomEnum = Object.keys(enumLabelMap).includes(id);

    const handleSelectAll = () => {
      const id = filtered.id;
      if (DIRECT_SELECT_LABELS.has(label)) {
        // Toggle between clearing the selection and selecting everything
        if (filtered.value.length > 0) {
          setFiltered(id, []);
        } else {
          setFiltered(id, [...newData]);
        }
      } else if (EXCLUSION_SELECT_LABELS.has(label)) {
        if (filtered.value.length === newData.length) {
          setFiltered(id, []);
        } else {
          setFiltered(id, [...newData]);
        }
      } else if (EXCLUSION_STORED_IDS.has(id)) {
        // filtered.value = excluded options, and setFiltered wants the selected ones.
        // Something excluded -> select all; nothing excluded -> select none.
        setFiltered(id, filtered.value.length > 0 ? [...newData] : []);
      } else if (isCustomEnum) {
        const allSelected = filtered.value.length === 0;
        setFiltered(id, allSelected ? [...data] : []);
      } else {
        setFiltered(id, []);
      }
    };
  // const handleSelectAll = () => {
  //   const id = filtered.id;

  //   // Special handling for "Needs/Surplus" - toggle between all selected and none selected
  //   if (label === "Needs/Surplus") {
  //     // If any items are selected, clear all selections
  //     if (filtered.value.length > 0) {
  //       setFiltered(id, []);
  //     } else {
  //       // If nothing is selected, select all options
  //       setFiltered(id, [...newData]);
  //     }
  //   } else {
  //     // Original behavior for other filters
  //     setFiltered(id, []);
  //   }
  // };

  const handleReset = () => {
    const id = filtered.id;
    setFiltered(id, data);
  };

  return (
    <>
      <div className=" h-[1px] w-full border-x-0  border-t border-b-0 border-solid border-[#D8D8D8] px-2" />
      <div className="my-0 px-2 mb-2">
        <div className="flex justify-between items-start">
          <label
            style={{
              fontWeight: "bold",
              color: "gray",
              fontSize: "12px",
              marginBottom: "5px",
            }}
          >
            {label.toUpperCase()}
          </label>
          <div className="flex items-start">
            <button
              className="border-none text-xs font-medium bg-transparent py-0 rounded-2xl hover:text-gray-400 p-0"
              onClick={handleSelectAll}
            >
              {filtered?.value?.length > 0 ? (
                <CheckSquare height={"14px"} />
              ) : (
                <XSquare height={"14px"} />
              )}
            </button>
          </div>
        </div>
        <div className="mt-0">
          {newData.map((item, index) => {
            // Get the value property from the filters
            const val = filtered["value"];
            
            // Check if the item is included in the value array
            const isFiltered = val.includes(item);
            const toggleCustomEnum = (clickedValue) => {
              let excluded = [...filtered.value]; // always treat this as "excluded values"
              const isExcluded = excluded.includes(clickedValue);
              if (isExcluded) {
                // If it's already excluded, remove from the exclusion list (i.e. select it)
                excluded = excluded.filter(v => v !== clickedValue);
              } else {
                // Exclude it (i.e. unselect it)
                excluded.push(clickedValue);
              }
              // If nothing is excluded anymore, reset to []
              if (excluded.length === 0) excluded = [];
              amplitude.track({
                event_type: `v1_filter_${id.replaceAll(" ", "_")}`,
                event_properties: { values: excluded },
              });
              setFiltered(id, excluded);
            };
            // Handle filtration with special case for Needs/Surplus
            const handleFilteration = (selectedValue) => {
              const filterId = filtered.id;

              if (DIRECT_SELECT_LABELS.has(label)) {
                let selectedItems = [...filtered.value];
                selectedItems = selectedItems.includes(selectedValue)
                  ? selectedItems.filter((item) => item !== selectedValue)
                  : [...selectedItems, selectedValue];

                amplitude.track({
                  event_type: `v1_filter_${filterId.toLowerCase().replaceAll(" ", "_")}`,
                  event_properties: { values: selectedItems },
                });
                setFiltered(filterId, selectedItems);
                return;
              }

              if (EXCLUSION_SELECT_LABELS.has(label)) {
                let excludedItems = [...filtered.value];
                excludedItems = excludedItems.includes(selectedValue)
                  ? excludedItems.filter((item) => item !== selectedValue)
                  : [...excludedItems, selectedValue];

                amplitude.track({
                  event_type: `v1_filter_${filterId.toLowerCase().replaceAll(" ", "_")}`,
                  event_properties: { values: excludedItems },
                });
                setFiltered(filterId, excludedItems);
                return;
              }

              const excludedBefore = filtered.value;
              const isRangeLabel = RANGE_LABELS.has(label);

              if (isRangeLabel) {
                const clickedIdx = newData.indexOf(selectedValue);
                const selectedNowRaw = newData.filter(
                  (opt) => !excludedBefore.includes(opt)
                );

                let selectedRange = selectedNowRaw;
                if (selectedNowRaw.length > 1) {
                  const indices = selectedNowRaw
                    .map((v) => newData.indexOf(v))
                    .filter((i) => i !== -1)
                    .sort((a, b) => a - b);
                  selectedRange = newData.slice(indices[0], indices[indices.length - 1] + 1);
                }

                if (selectedRange.length === 0) {
                  const next = [selectedValue];
                  amplitude.track({
                    event_type: `v1_filter_${filterId.toLowerCase().replaceAll(" ", "_")}`,
                    event_properties: { values: next },
                  });
                  setFiltered(filterId, next);
                  return;
                }

                const curStartIdx = newData.indexOf(selectedRange[0]);
                const curEndIdx = newData.indexOf(
                  selectedRange[selectedRange.length - 1]
                );

                if (clickedIdx < curStartIdx) {
                  const next = newData.slice(clickedIdx, curEndIdx + 1);
                  amplitude.track({
                    event_type: `v1_filter_${filterId.toLowerCase().replaceAll(" ", "_")}`,
                    event_properties: { values: next },
                  });
                  setFiltered(filterId, next);
                  return;
                }
                if (clickedIdx > curEndIdx) {
                  const next = newData.slice(curStartIdx, clickedIdx + 1);
                  amplitude.track({
                    event_type: `v1_filter_${filterId.toLowerCase().replaceAll(" ", "_")}`,
                    event_properties: { values: next },
                  });
                  setFiltered(filterId, next);
                  return;
                }
                if (clickedIdx === curStartIdx && clickedIdx === curEndIdx) {
                  amplitude.track({
                    event_type: `v1_filter_${filterId.toLowerCase().replaceAll(" ", "_")}`,
                    event_properties: { values: [] },
                  });
                  setFiltered(filterId, []);
                  return;
                }
                if (clickedIdx === curStartIdx) {
                  const next = newData.slice(curStartIdx + 1, curEndIdx + 1);
                  amplitude.track({
                    event_type: `v1_filter_${filterId.toLowerCase().replaceAll(" ", "_")}`,
                    event_properties: { values: next },
                  });
                  setFiltered(filterId, next);
                  return;
                }
                if (clickedIdx === curEndIdx) {
                  const next = newData.slice(curStartIdx, curEndIdx);
                  amplitude.track({
                    event_type: `v1_filter_${filterId.toLowerCase().replaceAll(" ", "_")}`,
                    event_properties: { values: next },
                  });
                  setFiltered(filterId, next);
                  return;
                }
                return;
              }

              let currentlySelected = newData.filter(
                (opt) => !excludedBefore.includes(opt)
              );
              if (currentlySelected.includes(selectedValue)) {
                currentlySelected = currentlySelected.filter((item) => item !== selectedValue);
              } else {
                currentlySelected = [...currentlySelected, selectedValue];
              }

              amplitude.track({
                event_type: `v1_filter_${filterId.toLowerCase().replaceAll(" ", "_")}`,
                event_properties: { values: currentlySelected },
              });
              setFiltered(filterId, currentlySelected);
            };
            return (
              <TopFilterLabels
                key={index}
                value={item}
                isFiltered={
                  isCustomEnum
                    ? filtered.value.includes(item) // ✅ it's selected if not excluded
                    : filtered.value.includes(item)
                }
                count={countsMap[item]}
                setFiltered={() =>
                  isCustomEnum
                    ? toggleCustomEnum(item)
                    : handleFilteration(item)
                }
              />
            );
          })}
        </div>
      </div>
    </>
  );
};
export default TableFilter2;